<template>
  <el-autocomplete
    ref="autocomplete"
    class="search-input"
    popper-class="search-input"
    :size="size"
    v-model="searchKeyword"
    :fetch-suggestions="loadAutocompleteData"
    @select="emitSearchAction"
    value-key="objName"
    :debounce="300"
    :trigger-on-focus="true"
    @keyup.enter.native="emitSearchAction"
    :hide-loading="true"
    @keyup.native="handleInput"
  >
    <span class="el-input__icon" slot="suffix">
      <el-popover
        @show="showInfoPopper = true"
        @hide="showInfoPopper = false"
        popper-class="info"
        placement="top-start"
        width="320"
        trigger="hover">
          <i slot="reference" :class="['info-icon fad fa-question-circle', showInfoPopper ? 'show-info-popper' : '']" ></i>
          <span class="info-title">검색 대상 정보</span>
          <ul class="mr-t4">
            <li><span class="label">관리대상:</span>이름 / IP / 제조사</li>
            <li><span class="label">논리 그룹:</span>논리 그룹 이름</li>
            <li><span class="label">대시보드:</span>기본 대시보드 이름 / 커스텀 대시보드 이름</li>
            <li><span class="label">토폴로지맵:</span>토폴로지맵 이름</li>
            <li><span class="label">차트 템플릿:</span>차트 템플릿 이름</li>
            <li><span class="label">로그 필터:</span>로그 필터 이름</li>
            <li><span class="label">작업:</span>작업 이름</li>
            <li><span class="label">보고서:</span>보고서 이름</li>
          </ul>
      </el-popover>
      <i class="el-input__icon fas fa-arrow-right c-pointer" @click="emitSearchAction"></i>
    </span>
    <template slot-scope="{ item }">
      <!-- 검색 기록 -->
      <div v-if="isSearchHist" class="hist-item">
        <div class="label">{{ item.searchKeyword }}</div>
        <div>
          <span class="datetime">{{ getDateToString(item.checkDatetime, 'MM월 DD일') }}</span>
          <div class="delete-icon" @click.stop="deleteSearchHist(item.seqNumber)">
            <i class="fal fa-times"></i>
          </div>
        </div>
      </div>
      <!-- 자동완성 -->
      <div v-else class="autocomplete-item" v-html="highlightKeyword(item.searchKeyword, searchKeyword)" />
    </template>
  </el-autocomplete>
</template>

<script>
import { map, isNil, isEmpty, trim } from 'lodash';
import MsgUtil from '@/utils/message';
import SearchAPI from '@/api/frame/smart-search';
import { getDateToString } from '@/utils/date';
import { highlightKeyword } from '../utils/search';

export default {
  name: 'WSearchInput',
  props: {
    /**
     * 검색 키워드
     * @type {String}
     */
    keyword: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'medium ',
    },
  },
  data() {
    return {
      /**
       * 검색값
       * @type {[type]}
       */
      searchKeyword: this.keyword,
      /**
       * 최근 검색기록 , 자동완성 여부
       * @type {Boolean}
       */
      isSearchHist: true,
      /**
       * 최근 검색기록 로딩x / 자동완성 로딩o
       * @type {Boolean}
       */
      hideLoading: true,
      getDateToString,
      /**
       * 도움말 Popper 보이기 / 숨김 여부
       * @type {Boolean}
       */
      showInfoPopper: false,
    };
  },
  computed: {
    managerId() {
      return this.$store.getters.userInfo.managerId;
    },
  },
  watch: {
    keyword: {
      handler(to) {
        this.searchKeyword = to;
      },
    },
  },
  methods: {
    highlightKeyword,
    /**
     * autocomplete 의 fetch-suggestions 에 바인딩 될 메소드
     * 검색어가 없을 때에는 최근 검색 기록을 조회하며
     * 검색어 입력시 검색어로 시작하는 자동완성 리스트를 조회한다.
     * @author Hunui
     * @param  {[type]}   queryString [description]
     * @param  {Function} callback    [description]
     */
    loadAutocompleteData(queryString, callback) {
      // 최근 검색 기록
      if (isNil(queryString) || trim(queryString) === '') {
        const params = {
          managerId: this.managerId,
        };
        const action = MsgUtil.action.VIEW;
        const target = '최근 검색 기록';
        SearchAPI.getSearchHist(params)
          .then((response) => {
            if (response.data.success) {
              callback(response.data.data);
            } else {
              this.$message(MsgUtil.getResultMsgData(action, target, false));
            }
          })
          .catch((e) => {
            this.$message(MsgUtil.getResultMsgData(action, target, false));
            throw new Error(e);
          })
          .finally(() => {
            if (isEmpty(this.searchKeyword)) {
              this.isSearchHist = true;
            }
          });
      } else {
        // 자동 완성
        const action = MsgUtil.action.VIEW;
        const target = '자동 완성 검색어';
        SearchAPI.getAutoCompletionList(queryString)
          .then((response) => {
            if (response.data.success) {
              const data = response.data.data;
              if (data.keyword !== this.searchKeyword) {
                // 네트워크 지연으로 인해 이전에 입력한 키워드 값으로 수신한 자동완성 값은
                // 바인딩하지 않도록 합니다.
                return;
              }
              callback(
                map(data.autoCompletionList, (o) => ({
                  searchKeyword: o,
                })),
              );
            } else {
              this.$message(MsgUtil.getResultMsgData(action, target, false));
            }
          })
          .catch((e) => {
            this.$message(MsgUtil.getResultMsgData(action, target, false));
            throw new Error(e);
          })
          .finally(() => {
            if (!isEmpty(this.searchKeyword)) {
              this.isSearchHist = false;
            }
          });
      }
    },
    /**
     * slidingPopup 을 닫으면 suggestions 를 비운다.
     * @author Hunui
     */
    reset() {
      this.searchKeyword = '';
      this.$refs.autocomplete.suggestions = [];
      this.showInfoPopper = false;
    },
    /**
     * 검색 기록 선택, 자동완성 선택
     * 검색어 입력 후 검색 실행
     * @author Hunui
     * @param  {[type]} item [description]
     * @return {[type]}      [description]
     */
    emitSearchAction(item) {
      if (!isNil(item.searchKeyword)) {
        this.searchKeyword = item.searchKeyword;
      }
      const keyword = this.searchKeyword;

      if (keyword === null || isEmpty(keyword) || trim(keyword) === '') {
        this.$message(MsgUtil.getCustomMsgData('검색어를 입력해주십시오'));
        return;
      }
      this.$refs.autocomplete.close();
      this.$refs.autocomplete.$refs.input.blur();
      this.$emit('search-action', keyword);
    },
    /**
     * 최근 검색기록 삭제
     * @author Hunui
     * @param  {[type]} seqNumber 검색기록 seqNumber
     */
    deleteSearchHist(seqNumber) {
      const action = MsgUtil.action.DELETE;
      const target = '최근 검색 기록';
      SearchAPI.deleteSearchHist(seqNumber)
        .then((response) => {
          if (response.data.success) {
            this.$message(MsgUtil.getResultMsgData(action, target, true));
            this.$refs.autocomplete.getData(this.searchKeyword);
          } else {
            this.$message(MsgUtil.getResultMsgData(action, target, false));
          }
        })
        .catch((e) => {
          this.$message(MsgUtil.getResultMsgData(action, target, false));
          throw new Error(e);
        });
    },
    handleInput(item) {
      this.$refs.autocomplete.handleChange(item.target.value);
    },
  },
};
</script>

<style></style>



// WEBPACK FOOTER //
// src/views/frame/components/popup/SmartSearchPopup/components/SearchInput.vue